import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import { socialLinks, getSocialIcon, usesReactIcon } from '../config/socialLinks';

const Footer = () => {
  const [email, setEmail] = useState('');

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement newsletter subscription
    console.log('Newsletter subscription:', email);
    setEmail('');
  };

  const handleWhatsAppClick = () => {
    const message = "Hi! I'm interested in your email validation services. Can you help me get started?";
    const whatsappUrl = `https://wa.me/917574082582?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleEmailClick = () => {
    const subject = "Email Validation Services Inquiry";
    const body = "Hi Octalchip team,\n\nI'm interested in your email validation services. Could you please provide more information about your pricing and features?\n\nBest regards,\n[Your Name]";
    const mailtoUrl = `mailto:business@octalchip.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  };

  return (
    <footer className="bg-surface border-t border-border mt-16">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-7xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Company Info */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center space-x-2">
              <img src="/octalchiplogo.svg" alt="Octalchip Logo - Professional Email Validation Service" className="h-7 sm:h-8 w-auto" />
            </div>
            <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
              Empowering businesses with cutting-edge email validation, AI-powered verification, and cloud solutions for a digital future.
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center sm:justify-start">
              {socialLinks
                .filter(link => 
                  link.name !== 'Email' && 
                  link.name !== 'WhatsApp' && 
                  link.name !== 'YouTube'
                )
                .map((social, index) => {
                const ReactIcon = getSocialIcon(social);
                // Prioritize react-icon if available, otherwise use Lucide icon, fallback to text
                const renderIcon = () => {
                  if (ReactIcon) {
                    return <ReactIcon className="w-5 h-5 sm:w-5 sm:h-5" />;
                  }
                  if (social.icon) {
                    return <Icon name={social.icon} className="w-5 h-5 sm:w-5 sm:h-5" />;
                  }
                  // Fallback: show first letter of platform name
                  return <span className="text-xs font-semibold">{social.name.charAt(0)}</span>;
                };
                
                return (
                  <a
                    key={index}
                    href={social.url}
                    target={social.url.startsWith('mailto:') ? '_self' : '_blank'}
                    rel={social.url.startsWith('mailto:') ? '' : 'noopener noreferrer'}
                    className="w-10 h-10 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-surface hover:bg-primary/10 text-text-secondary hover:text-primary transition-all duration-200 ease-in-out active:scale-95 touch-manipulation focus:outline-none focus:ring-2 focus:ring-primary/20"
                    title={social.name}
                    aria-label={social.name}
                  >
                    {renderIcon()}
                  </a>
                );
              })}
            </div>
          </div>

          {/* Company Links */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Company</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/docs" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  API Documentation
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Resources</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/docs" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  API Documentation
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/cancellation-refund" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Cancellation & Refund
                </Link>
              </li>
              <li>
                <Link to="/shipping-delivery" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Service Delivery
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>
            
            {/* Contact Info */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Contact</h4>
            <div className="space-y-2.5 sm:space-y-3">
              <div className="flex items-start space-x-2">
                <Icon name="MapPin" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <div className="text-xs sm:text-sm text-text-secondary">
                  <p className="font-medium text-text-primary">Head Office</p>
                  <p>Octalchip, 404, Opera point,</p>
                  <p>kothariya, gujarat, india, 360022</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={16} className="text-primary flex-shrink-0" />
                <a 
                  href="mailto:business@octalchip.com" 
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors break-all"
                >
                  business@octalchip.com
                </a>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={16} className="text-primary flex-shrink-0" />
                <a 
                  href="tel:+917574082582" 
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors"
                >
                  +91 7574082582
                </a>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-primary flex-shrink-0" />
                <span className="text-xs sm:text-sm text-text-secondary">Mon - Fri, 9AM - 6PM</span>
              </div>
            </div>
              </div>
            </div>

        {/* All Buttons and Newsletter */}
        <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-center">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                iconName="MessageCircle"
                animation="wiggle"
                className="border-primary text-primary bg-white hover:bg-primary hover:text-white w-full sm:w-auto justify-center"
                onClick={handleWhatsAppClick}
              >
                WhatsApp Us
              </Button>
            
              <Button
                variant="outline"
                size="sm"
                iconName="Mail"
                animation="shake"
                className="border-primary text-primary bg-white hover:bg-primary hover:text-white w-full sm:w-auto justify-center"
                onClick={handleEmailClick}
              >
                Email Us
              </Button>
            </div>

            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2 sm:gap-2 w-full sm:w-auto sm:flex-1 sm:max-w-md">
              <input
                type="email"
                placeholder="Subscribe our newsletter to stay updated"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-3 py-2 border border-primary rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary/20 focus:border-primary w-full sm:flex-1 min-w-0"
                required
              />
              <Button
                type="submit"
                variant="outline"
                size="sm"
                animation="zoom"
                className="border-primary text-primary bg-white hover:bg-primary hover:text-white w-full sm:w-auto justify-center whitespace-nowrap"
              >
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="bg-primary text-white py-3 sm:py-4">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl text-center">
          <p className="text-xs sm:text-sm leading-relaxed">
            Copyright © 2025 EmailValidator | Powered by{' '}
            <a 
              href="https://www.octalchip.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline font-medium transition-opacity hover:opacity-90"
            >
              Octalchip
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 